import React, { useMemo, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import {
  CHART_SCORE_MONETIZATION,
  CHART_SCORE_OPERATIONAL,
  CHART_SCORE_OVERALL,
} from "../../../../styles/theme_constants";
import type { FeeSource, KpiMonth } from "../../../../types/chargingStationDashboard";
import {
  formatEuro,
  formatNumber,
  formatPercent,
  formatSignedEuro,
  formatSignedNumber,
  formatSignedPercent,
  safeDivide,
} from "../../../../utils/fleetDerivations";
import { lastCompletedAnchor, monthKeyOf, monthLabel, previousMonthKey, shortMonthLabel } from "../../../../utils/fleetPeriods";
import { formatScore } from "../../../../utils/scorePresentation";
import { useFleetKpi } from "../hooks/useFleetKpi";
import { baseChartOptions, CHART_HEIGHT, lineSeries } from "./dashboardChart";
import ScoreBreakdown from "./ScoreBreakdown";
import StatTile from "./StatTile";
import UnavailableNotice from "./UnavailableNotice";

const FEE_SOURCE_LABEL: Record<FeeSource, string> = {
  INVOICED: "As invoiced on the month's credit notes.",
  IMPORTED: "From the workbook's hand-kept history, which ran about 10% below the invoiced fee.",
  ESTIMATED: "Estimated: not invoiced yet.",
};

const FEE_SOURCE_HINT: Record<FeeSource, string> = {
  INVOICED: "cocharge's commission on the month's credit notes, at each contract's own rate on net amounts.",
  IMPORTED:
    "The service fee the workbook recorded for this month. From May to August 2026 the workbook's figure was 9–12% below the fee actually invoiced, so compare it with billed months with care.",
  ESTIMATED:
    "The configured share of the month's charging revenue — within 0.3% of the invoiced fee from May to August 2026 — replaced by the invoiced fee once the month's credit notes are generated.",
};

interface KpiScorecardProps {
  /** Switches the page to its existing recompute tab — offered when the year was never computed. */
  onRecompute: () => void;
}

/**
 * The three scores across the months of the chosen year (FR-053), on one 0–100 axis. An unavailable
 * score leaves a gap rather than dropping to zero. Two of the three line colours sit below 3:1 on white,
 * so the same scores are also given as a table.
 */
const ScoreTrend: React.FC<{ months: KpiMonth[]; year: number }> = ({ months, year }) => {
  const data = useMemo(
    () => ({
      labels: months.map((m) => shortMonthLabel(m.month)),
      datasets: [
        lineSeries("Overall", months.map((m) => m.scores.overall), CHART_SCORE_OVERALL),
        lineSeries("Operational", months.map((m) => m.scores.operational?.value ?? null), CHART_SCORE_OPERATIONAL),
        lineSeries("Monetization", months.map((m) => m.scores.monetization?.value ?? null), CHART_SCORE_MONETIZATION),
      ],
    }),
    [months],
  );

  return (
    <Card title={`Scores across ${year}`} className="mt-3">
      <Chart type="line" data={data} options={baseChartOptions({ yTitle: "Score" })} style={CHART_HEIGHT} />
      <DataTable
        value={months}
        dataKey="month"
        size="small"
        className="text-sm mt-3"
        pt={{ table: { "aria-label": `Scores across ${year}` } }}
      >
        <Column header="Month" body={(m: KpiMonth) => monthLabel(m.month)} />
        <Column header="Overall" body={(m: KpiMonth) => formatScore(m.scores.overall)} />
        <Column header="Operational" body={(m: KpiMonth) => formatScore(m.scores.operational?.value)} />
        <Column header="Monetization" body={(m: KpiMonth) => formatScore(m.scores.monetization?.value)} />
        <Column
          header="Note"
          body={(m: KpiMonth) =>
            m.invoicedClients == null ? "No completed credit-note run" : !m.complete ? "Running month" : ""
          }
        />
      </DataTable>
    </Card>
  );
};

/**
 * BOF-76 — the monthly KPI scorecard (US5), replacing the workbook's `KPI_2026` sheet.
 *
 * Opens on the most recent completed month, like the rest of the dashboard: the running month has no
 * credit-note run yet, so it has no client count and no scores.
 *
 * Invoiced clients come from the month's completed credit-note run and nowhere else (FR-044). Until that
 * run completes the count is absent, and so is everything derived from it — fee per client, client
 * growth, churn and both scores — rather than a figure computed over some other population (FR-045).
 * Churn is shown only in a month where the client count fell; a month where it rose has no churn, which
 * is not the same as a churn of zero (FR-046).
 */
const KpiScorecard: React.FC<KpiScorecardProps> = ({ onRecompute }) => {
  const [anchor, setAnchor] = useState<Date>(() => lastCompletedAnchor("MONTH"));
  const year = anchor.getFullYear();
  const key = monthKeyOf(anchor);
  const { loading, error, neverComputed, months, parameters, reload } = useFleetKpi(year);
  const selected = months.find((m) => m.month === key) ?? null;
  const comparedTo = `vs ${monthLabel(previousMonthKey(key))}`;

  return (
    <div>
      <div className="flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div className="flex flex-column gap-1">
          <label className="text-sm text-color-secondary" htmlFor="kpi-month">
            Month
          </label>
          <Calendar
            inputId="kpi-month"
            value={anchor}
            onChange={(e) => e.value && setAnchor(e.value as Date)}
            view="month"
            dateFormat="MM yy"
            maxDate={new Date()}
            showIcon
          />
        </div>
        <span className="text-sm text-color-secondary">Commercial scorecard, monthly. Clients are those invoiced by the month's credit-note run.</span>
      </div>

      {loading && (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner style={{ width: 48, height: 48 }} />
        </div>
      )}

      {!loading && neverComputed && <UnavailableNotice reason={{ kind: "neverComputed" }} onRecompute={onRecompute} />}

      {!loading && error && (
        <Message
          severity="error"
          className="w-full justify-content-start"
          content={
            <span>
              {error}{" "}
              <button type="button" className="p-link text-primary ml-2" onClick={reload}>
                Retry
              </button>
            </span>
          }
        />
      )}

      {!loading && !neverComputed && !error && !selected && (
        <Message severity="info" className="w-full justify-content-start" text={`No figures for ${monthLabel(key)} yet.`} />
      )}

      {!loading && selected && parameters && (
        <>
          <div className="flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-xl font-semibold text-900">{monthLabel(selected.month)}</span>
            {!selected.complete && <Tag value="Running month" severity="warning" />}
          </div>

          {selected.invoicedClients == null && (
            <Message
              severity="warn"
              className="w-full justify-content-start mb-3"
              text={`No completed credit-note run for ${monthLabel(selected.month)} yet. Invoiced clients, every per-client figure, client growth, churn and both scores are unavailable — not zero.`}
            />
          )}
          {selected.partial && (
            <Message
              severity="warn"
              className="w-full justify-content-start mb-3"
              text="Some days of this month have no data, so the service fee and sessions are understated."
            />
          )}

          <div className="grid">
            <div className="col-12 xl:col-6">
              <Card title="Service fee" className="h-full">
                <div className="grid">
                  <div className="col-12 sm:col-6">
                    <StatTile
                      label="Service fee"
                      value={formatEuro(selected.serviceFeeCents)}
                      change={{ absolute: selected.feeChangeCents, percent: selected.inputs.feeGrowth }}
                      comparedTo={comparedTo}
                      formatAbsolute={formatSignedEuro}
                      hint={FEE_SOURCE_HINT[selected.serviceFeeSource ?? "ESTIMATED"]}
                    />
                    {selected.serviceFeeSource && (
                      <div className="text-xs text-color-secondary mt-1" data-testid="fee-source">
                        {FEE_SOURCE_LABEL[selected.serviceFeeSource]}
                      </div>
                    )}
                  </div>
                  <div className="col-6 sm:col-3">
                    <StatTile label="Year to date" value={formatEuro(selected.serviceFeeYtdCents)} />
                  </div>
                  <div className="col-6 sm:col-3">
                    <StatTile label="Paid sessions" value={formatNumber(selected.paidSessions)} />
                  </div>
                </div>
              </Card>
            </div>

            <div className="col-12 xl:col-6">
              <Card title="Clients" className="h-full">
                <div className="grid">
                  <div className="col-6 sm:col-3">
                    <StatTile label="Invoiced clients" value={formatNumber(selected.invoicedClients)} />
                  </div>
                  <div className="col-6 sm:col-3">
                    <StatTile label="Net new clients" value={formatSignedNumber(selected.inputs.netNewClients)} />
                  </div>
                  <div className="col-6 sm:col-3">
                    <StatTile
                      label="Client growth"
                      value={formatSignedPercent(selected.inputs.clientGrowth)}
                      hint={comparedTo}
                    />
                  </div>
                  {selected.churnRate != null && (
                    <div className="col-6 sm:col-3">
                      <StatTile
                        label="Churn rate"
                        value={formatPercent(selected.churnRate)}
                        hint="Share of last month's invoiced clients not invoiced this month. Shown only when the count fell."
                      />
                    </div>
                  )}
                </div>
                <div className="text-xs text-color-secondary" data-testid="clients-basis">
                  {selected.invoicedClientsSource === "IMPORTED"
                    ? "From the workbook's hand-kept history (before the credit-note run history)."
                    : selected.invoicedClientsSource === "BILLING"
                      ? `Counted as ${parameters.clientBasis === "CREDIT_NOTES" ? "credit notes" : "distinct customers"} on the month's credit-note run: ${formatNumber(selected.distinctCustomers)} distinct customers · ${formatNumber(selected.creditNotes)} credit notes.`
                      : null}
                </div>
              </Card>
            </div>

            <div className="col-12 xl:col-6">
              <Card title="Charge points" className="h-full">
                <div className="grid">
                  <div className="col-6 sm:col-4">
                    <StatTile label="Online" value={formatNumber(selected.chargePointsOnline)} />
                  </div>
                  <div className="col-6 sm:col-4">
                    <StatTile label="Total" value={formatNumber(selected.chargePointsTotal)} />
                  </div>
                  <div className="col-6 sm:col-4">
                    <StatTile
                      label="Activation rate"
                      value={formatPercent(selected.inputs.activationRate)}
                      hint="Charge points online as a share of all charge points — the online rate, under the scorecard's name."
                    />
                  </div>
                  <div className="col-6 sm:col-4">
                    <StatTile label="Online per client" value={formatNumber(selected.inputs.cpOnlinePerClient, 2)} />
                  </div>
                  <div className="col-6 sm:col-4">
                    <StatTile label="Total per client" value={formatNumber(selected.inputs.cpTotalPerClient, 2)} />
                  </div>
                </div>
                {selected.chargePointsSource === "IMPORTED" && (
                  <div className="text-xs text-color-secondary">
                    Charge-point figures for this month come from the workbook's hand-kept monthly history.
                  </div>
                )}
              </Card>
            </div>

            <div className="col-12 xl:col-6">
              <Card title="Service fee per" className="h-full">
                <div className="grid">
                  <div className="col-12 sm:col-4">
                    <StatTile label="Per client" value={formatEuro(selected.inputs.feePerClient, 2)} />
                  </div>
                  <div className="col-6 sm:col-4">
                    <StatTile
                      label="Per charge point online"
                      value={formatEuro(safeDivide(selected.serviceFeeCents, selected.chargePointsOnline), 2)}
                    />
                  </div>
                  <div className="col-6 sm:col-4">
                    <StatTile label="Per charge point total" value={formatEuro(selected.inputs.feePerCpTotal, 2)} />
                  </div>
                </div>
                {selected.chargePointsTotal == null && (
                  <div className="text-sm text-color-secondary">No charge-point snapshot was recorded for this month.</div>
                )}
              </Card>
            </div>
          </div>

          <ScoreBreakdown month={selected} parameters={parameters} />
          <ScoreTrend months={months} year={year} />
        </>
      )}
    </div>
  );
};

export default KpiScorecard;
